import { useEffect, useState } from 'react';
import { Button, Progress, Spin, Tooltip } from 'antd';
import {
  DownOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import { creditLineEndpoints, CreditLine, CreditLineEligibility, DrawRequest } from '../api/creditLineEndpoints';
import { useFeature } from '../contexts/FeatureFlagsContext';
import { formatCurrency } from '../utils/formatters';
import { LocTermsDrawer } from './LocTermsDrawer';
import { LocDrawRequestModal } from './LocDrawRequestModal';
import { useTranslation } from 'react-i18next';

function getStatusTone(status: CreditLine['status'] | 'eligible') {
  switch (status) {
    case 'active':
    case 'eligible':
      return 'green';
    case 'frozen':
      return 'gold';
    case 'closed':
    case 'inactive':
    default:
      return 'gray';
  }
}

export function LocCard() {
  const { t } = useTranslation('loc');
  const isLocEnabled = useFeature('line_of_credit');
  const [loading, setLoading] = useState(true);
  const [creditLine, setCreditLine] = useState<CreditLine | null>(null);
  const [pendingDraw, setPendingDraw] = useState<DrawRequest | null>(null);
  const [eligibility, setEligibility] = useState<CreditLineEligibility | null>(null);
  const [termsDrawerOpen, setTermsDrawerOpen] = useState(false);
  const [drawModalOpen, setDrawModalOpen] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [creditLineResponse, eligibilityResponse] = await Promise.all([
        creditLineEndpoints.getCreditLine(),
        creditLineEndpoints.getEligibility(),
      ]);

      setCreditLine(creditLineResponse.credit_line);
      setPendingDraw(creditLineResponse.pending_draw_request);
      setEligibility(eligibilityResponse.eligibility);
    } catch (error) {
      console.error('Failed to fetch LOC data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isLocEnabled && (!creditLine || creditLine.outstanding_balance_cents === 0)) {
    return null;
  }

  if (loading) {
    return (
      <section className="dashboard-loc-card dashboard-loading-state">
        <Spin tip={t('messages.loadingData')} />
      </section>
    );
  }

  if (!creditLine && eligibility && eligibility.eligible) {
    return (
      <>
        <section className="dashboard-loc-card">
          <div className="dashboard-loc-header">
            <div className="dashboard-card-title-group">
              <span className="dashboard-card-icon dashboard-card-icon--amber">
                <WalletOutlined />
              </span>
              <div className="dashboard-loc-copy">
                <div className="dashboard-loc-title-row">
                  <h2>{t('title')}</h2>
                  <span className={`dashboard-status-pill dashboard-status-pill--${getStatusTone('eligible')}`}>
                    {t('status.eligible')}
                  </span>
                </div>
                <p>{t('messages.eligibleDesc')}</p>
              </div>
            </div>

            <div className="dashboard-loc-actions">
              <Button type="primary" className="dashboard-primary-button" onClick={() => setTermsDrawerOpen(true)}>
                {t('buttons.reviewTerms')}
              </Button>
            </div>
          </div>

          <div className="dashboard-loc-metrics">
            <div className="dashboard-loc-metric dashboard-loc-metric--green">
              <div className="dashboard-loc-metric-label">{t('stats.basePrincipal')}</div>
              <strong>{formatCurrency(eligibility.base_principal_cents)}</strong>
            </div>
            <div className="dashboard-loc-metric dashboard-loc-metric--gold">
              <div className="dashboard-loc-metric-label">{t('stats.threshold')}</div>
              <strong>{formatCurrency(eligibility.threshold_cents)}</strong>
            </div>
            <div className="dashboard-loc-metric dashboard-loc-metric--purple">
              <div className="dashboard-loc-metric-label">{t('progress.label')}</div>
              <strong>{Math.min(eligibility.progress_percent, 100).toFixed(0)}%</strong>
            </div>
          </div>
        </section>

        <LocTermsDrawer
          open={termsDrawerOpen}
          onClose={() => setTermsDrawerOpen(false)}
          onAccept={() => {
            setTermsDrawerOpen(false);
            fetchData();
          }}
        />
      </>
    );
  }

  if (!creditLine) {
    return null;
  }

  const getStatusLabel = () => {
    switch (creditLine.status) {
      case 'active':
        return t('status.active');
      case 'frozen':
        return t('status.frozen');
      case 'closed':
        return t('status.closed');
      case 'inactive':
      default:
        return t('status.inactive');
    }
  };

  const getStatePanel = () => {
    if (!isLocEnabled && creditLine.outstanding_balance_cents > 0) {
      return <div className="dashboard-loc-panel">{t('messages.featureUnavailableDesc')}</div>;
    }

    if (eligibility && !eligibility.eligible && creditLine.terms_status !== 'accepted') {
      return (
        <div className="dashboard-loc-panel">
          <strong>{t('messages.notEligible')}</strong>
          <div>{t('messages.notEligibleDesc')}</div>
          <div className="dashboard-loc-progress">
            <Progress percent={Math.min(eligibility.progress_percent, 100)} showInfo={false} />
          </div>
          <div className="dashboard-loc-panel-list">
            <span>
              {formatCurrency(eligibility.base_principal_cents)} / {formatCurrency(eligibility.threshold_cents)}
            </span>
          </div>
        </div>
      );
    }

    if (pendingDraw) {
      return (
        <div className="dashboard-loc-panel">
          <strong>{t('messages.pendingRequest')}</strong>
          <div>{t('messages.pendingRequestDesc')}</div>
          <div className="dashboard-loc-panel-list">
            <span>
              {t('progress.amount')}: {formatCurrency(pendingDraw.requested_amount_cents)}
            </span>
            <span>
              {t('progress.status')}: {t('progress.pendingApproval')}
            </span>
          </div>
        </div>
      );
    }

    if (creditLine.terms_status === 'eligible') {
      return <div className="dashboard-loc-panel">{t('messages.eligibleDesc')}</div>;
    }

    return null;
  };

  const getDrawButtonTooltip = () => {
    if (!isLocEnabled) {
      return t('tooltips.unavailable');
    }

    if (creditLine.status === 'frozen') {
      return t('tooltips.frozen');
    }

    if (creditLine.status === 'closed') {
      return t('tooltips.closed');
    }

    if (creditLine.available_credit_cents === 0) {
      return t('tooltips.noCredit');
    }

    if (pendingDraw) {
      return t('messages.pendingRequestDesc');
    }

    if (creditLine.terms_status !== 'accepted') {
      return t('messages.mustAcceptTerms');
    }

    return null;
  };

  const isDrawDisabled =
    !isLocEnabled ||
    creditLine.status !== 'active' ||
    creditLine.available_credit_cents === 0 ||
    Boolean(pendingDraw) ||
    creditLine.terms_status !== 'accepted';

  const requestCreditButton = (
    <Button
      type="primary"
      className="dashboard-primary-button"
      disabled={isDrawDisabled}
      onClick={() => setDrawModalOpen(true)}
    >
      {t('buttons.requestCredit')}
    </Button>
  );

  return (
    <>
      <section className="dashboard-loc-card">
        <div className="dashboard-loc-header">
          <div className="dashboard-card-title-group">
            <span className="dashboard-card-icon dashboard-card-icon--amber">
              <WalletOutlined />
            </span>
            <div className="dashboard-loc-copy">
              <div className="dashboard-loc-title-row">
                <h2>{t('title')}</h2>
                <span className={`dashboard-status-pill dashboard-status-pill--${getStatusTone(creditLine.status)}`}>
                  {getStatusLabel()}
                </span>
              </div>
              <p>
                {creditLine.terms_status === 'accepted'
                  ? t('messages.eligible')
                  : creditLine.terms_status === 'eligible'
                    ? t('messages.eligibleDesc')
                    : t('messages.notEligibleDesc')}
              </p>
            </div>
          </div>

          <div className="dashboard-loc-actions">
            {creditLine.terms_status === 'eligible' && (
              <Button type="primary" className="dashboard-primary-button" onClick={() => setTermsDrawerOpen(true)}>
                {t('buttons.reviewTerms')}
              </Button>
            )}

            {creditLine.terms_status === 'accepted' && !pendingDraw &&
              (getDrawButtonTooltip() ? (
                <Tooltip title={getDrawButtonTooltip()}>{requestCreditButton}</Tooltip>
              ) : (
                requestCreditButton
              ))}
          </div>
        </div>

        <div className="dashboard-loc-metrics">
          <div className="dashboard-loc-metric">
            <div className="dashboard-loc-metric-label">
              <span>{t('metrics.creditLimit')}</span>
              <DownOutlined />
            </div>
            <strong>{formatCurrency(creditLine.credit_limit_cents)}</strong>
          </div>

          <div className="dashboard-loc-metric dashboard-loc-metric--green">
            <div className="dashboard-loc-metric-label">{t('metrics.currentBalance')}</div>
            <strong>{formatCurrency(creditLine.outstanding_balance_cents)}</strong>
          </div>

          <div className="dashboard-loc-metric dashboard-loc-metric--purple">
            <div className="dashboard-loc-metric-label">{t('metrics.availableCredit')}</div>
            <strong>{formatCurrency(creditLine.available_credit_cents)}</strong>
          </div>
        </div>

        {getStatePanel()}
      </section>

      <LocTermsDrawer
        open={termsDrawerOpen}
        onClose={() => setTermsDrawerOpen(false)}
        onAccept={async () => {
          setTermsDrawerOpen(false);
          await fetchData();
        }}
      />

      <LocDrawRequestModal
        open={drawModalOpen}
        onClose={() => setDrawModalOpen(false)}
        maxAmount={creditLine.available_credit_cents}
        onSuccess={async () => {
          setDrawModalOpen(false);
          await fetchData();
        }}
      />
    </>
  );
}

export default LocCard;